import React from "react";
import { cn } from "@/lib/utils";
import External from "@/app/assets/icons/external";
import ChevronRight from "@/app/assets/icons/chevron-right";
import TrackedLink from "@/app/components/icm 3.0/shared/TrackedLink";

interface ProductCardProps {
  badge?: string;
  badgeVariant?: "default" | "orange" | "blue" | "green" | "yellow";
  title: React.ReactNode;
  description: React.ReactNode;
  attribution?: string;
  ctaText?: string;
  ctaHref?: string;
  analyticsEvent?: string;
  ctaClassName?: string;
  ctaArrow?: "northeast" | "chevron";
  newLabel?: string;
  textSize?: string;
  className?: string;
  contentClassName?: string;
  badgeClassName?: string;
  descriptionClassName?: string;
  illustrationClassName?: string;
  children?: React.ReactNode;
}

export function ProductCard({
  badge,
  badgeVariant = "default",
  title,
  description,
  attribution,
  ctaText = "Start Trading",
  ctaHref = "#",
  analyticsEvent,
  ctaClassName,
  ctaArrow = "northeast",
  newLabel,
  textSize = "text-3xl",
  className,
  contentClassName,
  badgeClassName,
  descriptionClassName,
  illustrationClassName,
  children,
}: ProductCardProps) {
  return (
    <div
      className={cn(
        "relative flex min-h-0 overflow-hidden rounded-2xl border border-[#E6E6E6] bg-[#fff8f4] p-6 md:p-[23px]",
        className
      )}
    >
      {/* New Label — desktop only (Figma solid badge) */}
      {newLabel && (
        <div className="absolute right-[23px] top-[23px] z-20 hidden md:block">
          <span className="inline-flex h-[26px] items-center justify-center rounded-lg bg-brand-primary-500 px-1.5 text-base font-medium leading-[22px] text-white">
            {newLabel}
          </span>
        </div>
      )}

      {/* Content — Figma: column gap 24 */}
      <div
        className={cn(
          "relative z-10 flex max-w-[340px] flex-col gap-6",
          contentClassName
        )}
      >
        {badge && (
          <span
            className={cn(
              "inline-flex h-[30px] w-fit items-center justify-center rounded-lg border px-3 py-1 text-sm font-medium leading-5 tracking-[0]",
              getBadgeColor(badgeVariant),
              badgeClassName
            )}
          >
            {badge}
          </span>
        )}

        <div className="flex flex-col gap-2 tracking-[0] md:gap-3">
          <h3 className={cn("font-bold text-black", textSize)}>{title}</h3>

          <p
            className={cn(
              "text-base font-normal leading-5 text-[#767676] md:text-lg md:leading-6",
              descriptionClassName
            )}
          >
            {description}
          </p>

          {attribution ? (
            <p className="text-base font-semibold leading-6 text-[#292929] md:text-lg">
              {attribution}
            </p>
          ) : null}
        </div>

        {ctaText && (
          <TrackedLink
            href={ctaHref}
            event={analyticsEvent}
            className={cn(
              "inline-flex w-fit items-center gap-1 text-sm font-medium leading-5 underline",
              ctaClassName ?? "text-ink-link"
            )}
          >
            {ctaText}
            {ctaArrow === "chevron" ? (
              <ChevronRight className="size-4" aria-hidden />
            ) : (
              <External className="size-4" aria-hidden />
            )}
          </TrackedLink>
        )}
      </div>

      {children ? (
        <div
          className={cn(
            "pointer-events-none z-0",
            illustrationClassName ??
              "absolute bottom-3 right-3 md:bottom-4 md:right-5"
          )}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

function getBadgeColor(variant: ProductCardProps["badgeVariant"]) {
  switch (variant) {
    case "orange":
      return "border-[#FDE7D7] bg-white text-brand-primary-500";

    case "blue":
      return "border-[#C5D8F8] bg-[#FAFAFA] text-[#375ED9]";

    case "green":
      return "border-[#BBF7D0] bg-[#FAFAFA] text-[#16A34A]";

    case "yellow":
      return "border-[#FFE545] bg-[#FAFAFA] text-[#A26506]";

    default:
      return "border-[#E6E6E6] bg-white text-gray-700";
  }
}
